import { useRef } from 'react';
import { Menu, MenuItem, MenuToggle, KeenIcon } from '@/components';
import { DropdownUser } from '@/partials/dropdowns/user';
import { DropdownNotifications } from '@/partials/dropdowns/notifications';
import { DropdownCard2 } from '@/partials/dropdowns/general';
import { useLanguage } from '@/i18n';
import { isReferralAttorney, shouldHideTopbarActions, useAuthContext } from '@/auth';
import { useSocket } from '@/providers/SocketProvider';
import { useAbility } from '@/providers';
import { useResponsive } from '@/hooks';
import { GTranslate } from '../../../i18n';

const HeaderTopbar = () => {
  const { auth } = useAuthContext();
  const desktopMode = useResponsive('up', 'lg');
  const itemChatRef = useRef(null);
  const itemNotificationsRef = useRef(null);
  const itemUserRef = useRef(null);
  const { isRTL } = useLanguage();
  const { notifications } = useSocket()
  const unreadCount = notifications.filter(n => !n.read_at).length;
  const user = auth?.user;
  const fullName = (auth?.user?.first_name || auth?.user?.last_name)
    ? `${auth?.user?.first_name || ''} ${auth?.user?.last_name || ''}`.trim()
    : (auth?.user?.name || 'N/A');
  const isReferral = isReferralAttorney(user);
  const { ability } = useAbility();
  const canCreateContact = ability.can('handle', 'contact')
  const canCreateMatter = ability.can('handle', 'matter')
  const canCreateInvoice = ability.can('handle', 'invoice')
  const canCreateExpense = ability.can('handle', 'expense')
  const canCreateFlatFee = ability.can('handle', 'flatFee')
  const canHandleMails = ability.can('access', 'mass-mailing')
  const canAccessNotifications = ability.can('access', 'notification')
  const canShowMenu =
    !isReferral &&
    (
      canCreateContact ||
      canCreateMatter ||
      canCreateInvoice ||
      canCreateExpense ||
      canCreateFlatFee ||
      canHandleMails
    );
  const hideTopbarActions = shouldHideTopbarActions(user);

  const handleDropdownChatShow = () => {
    window.dispatchEvent(new Event('resize'));
  };
  return <div className="flex items-center gap-2 lg:gap-3.5 justify-end shrink-0"> {/* lg:w-[400px] */}
    <div className="flex items-center gap-2 me-0.5">
      {/* The picker is ~170px wide — too much for a phone header that also carries the
          hamburger, logo and dropdowns, where it ended up overlapping the hamburger.
          Below lg it moves into the mobile drawer instead. Google Translate binds to a
          single #google_translate_element, so exactly one of the two may be mounted;
          both sides gate on this same breakpoint. */}
      {desktopMode && (
        <div className='public-lang-switcher attorney-lang menu-default light:border-gray-300 !block !py-1 m-0 text-[10px]'>
          <GTranslate />
        </div>
      )}
      {(!canShowMenu || hideTopbarActions) ? (
        /* Render a hidden version to keep hooks alive during transition */
        <div className="hidden">
          {DropdownCard2()}
        </div>
      ) : (
        <Menu className="items-stretch">
          <MenuItem ref={itemChatRef} onShow={handleDropdownChatShow} toggle="dropdown" trigger="click" dropdownProps={{
            placement: isRTL() ? 'bottom-start' : 'bottom-end',
            modifiers: [{
              name: 'offset',
              options: {
                offset: [0, 10] // [skid, distance]
              }
            }]
          }}>
            <MenuToggle className="btn btn-sm btn-dark">
              New
              <KeenIcon icon="down" />
            </MenuToggle>

            {DropdownCard2()}
          </MenuItem>
        </Menu>
      )}

      {canAccessNotifications && <div className="border-e border-gray-200 h-5"></div>}

      {/* DropdownNotifications is invoked as a plain function, so its hooks run
          as part of this component. It must be called on every render or the
          hook count changes (React error #310) — hence the hidden fallback.
          The permission gate for the fetch lives inside it. */}
      {canAccessNotifications && !hideTopbarActions ? (
        <Menu className="items-stretch">
          <MenuItem ref={itemNotificationsRef} toggle="dropdown" trigger="click" dropdownProps={{
            placement: isRTL() ? 'bottom-start' : 'bottom-end',
            modifiers: [{
              name: 'offset',
              options: {
                offset: isRTL() ? [7, 10] : [-7, 10] // [skid, distance]
              }
            }]
          }}>
            <MenuToggle className="relative btn btn-icon btn-icon-base btn-sm text-gray-600 hover:text-primary dropdown-open:text-primary">
              <KeenIcon icon="notification" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0 bg-danger-clarity text-danger text-[9px] font-medium leading-[0px] rounded-full size-4 flex items-center justify-center translate-x-1/2 -translate-y-1/2">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </MenuToggle>
            {DropdownNotifications({
              menuTtemRef: itemNotificationsRef
            })}
          </MenuItem>
        </Menu>
      ) : (
        /* Render a hidden version to keep hooks alive during transition */
        <div className="hidden">
          {DropdownNotifications({ menuTtemRef: itemNotificationsRef })}
        </div>
      )}

      {canAccessNotifications && !hideTopbarActions && <div className="border-e border-gray-200 h-5"></div>}

      <Menu className="items-stretch">
        <MenuItem ref={itemUserRef} toggle="dropdown" trigger="click" dropdownProps={{
          placement: isRTL() ? 'bottom-start' : 'bottom-end',
          modifiers: [{
            name: 'offset',
            options: {
              offset: isRTL() ? [7, 10] : [-7, 10] // [skid, distance]
            }
          }]
        }}>
          <MenuToggle className="!w-full btn btn-icon btn-icon-base btn-sm text-gray-600 hover:text-primary dropdown-open:text-primary">
            <KeenIcon icon="profile-circle" className="mr-[5px]" />
            <span className='max-[1124px]:hidden inline-block'>{fullName}</span>
          </MenuToggle>
          {DropdownUser({
            menuItemRef: itemUserRef
          })}
        </MenuItem>
      </Menu>
    </div>
  </div>;
};
export { HeaderTopbar };
